import { useRef, useState } from 'react';
import {
	Button as ToolbarButton,
	PasswordInput,
	DateTimeInput,
	AutocompleteInput,
	BooleanInput,
	SimpleForm,
	TextInput,
	required,
	useDataProvider,
	useGetIdentity,
	useNotify,
	useRecordContext,
	useRefresh,
	ReferenceArrayInput
} from 'react-admin';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { RoleChoices } from 'skylar/Roles';

// Disabled if current user is viewing themselves. Prevents priv escalation and lockout.
const RoleSelector = () => {
	const record = useRecordContext();
	const { data } = useGetIdentity();
	const disabled = record?.id === data.id || ( !data.role.includes( 'superadmin' ) && record?.role.includes( 'superadmin' ) );
	const choices = data.role.includes( 'superadmin' ) || disabled ? RoleChoices : RoleChoices.filter( (r) => r.id !== 'superadmin' );
	return <AutocompleteInput source="role" disabled={disabled} choices={choices} />
}

// Disabled if current user is viewing themselves. Prevents locking yourself out.
const ActiveSelector = () => {
	const record = useRecordContext();
	const { data } = useGetIdentity();
	const disabled = record?.id === data.id;
	return <BooleanInput source="active" disabled={disabled} defaultValue={true} />
}

export const FormFields = (
	<SimpleForm warnWhenUnsavedChanges>
		<TextInput source="email" validate={required()} />
		<PasswordInput source="new_password" />
		<TextInput source="name" validate={required()} />
		<TextInput source="position" />
		<ReferenceArrayInput reference="groups" source="group_ids" label="Groups" />
		<RoleSelector />
		<ActiveSelector />
		<DateTimeInput source="last_login" disabled fullWidth={false} />
	</SimpleForm>

);

const REQUIRED_COLUMNS = [
	{ name: 'email', desc: 'Required. Unique key used to match existing users.' },
	{ name: 'first name / last name', desc: 'Combined into the user’s name (a single "name" column is also accepted).' },
	{ name: 'password', desc: 'Optional. New users get a random password if left blank (shown below after import). Existing users keep their current password unless a value is supplied.' },
	{ name: 'position', desc: 'Optional, free text.' },
	{ name: 'role', desc: 'Optional: superadmin, admin, appadmin, eventhost, user. Defaults to "user".' },
	{ name: 'status', desc: 'Optional: "active" or "inactive". Blank leaves an existing user’s status unchanged; new users default to active.' },
	{ name: 'group1, group2, …', desc: 'Optional. Any column named "group" or "group<N>" assigns the user to a group, creating it if it doesn’t exist.' }
];

const ImportInstructions = () => (
	<>
		<Typography variant="body2" sx={{ mb: 2 }}>
			Upload a CSV file with a header row. Rows are matched to existing users by email:
			a match is updated, otherwise a new user is created.
		</Typography>
		<Accordion
			disableGutters
			sx={{
				mb: 2,
				bgcolor: 'action.hover',
				'&:before': { display: 'none' }
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
				<Typography variant="button" color="primary" sx={{ lineHeight: 1.5 }}>
					CSV column reference
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Column</TableCell>
							<TableCell>Notes</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{REQUIRED_COLUMNS.map(col => (
							<TableRow key={col.name}>
								<TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{col.name}</TableCell>
								<TableCell>{col.desc}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</AccordionDetails>
		</Accordion>
	</>
);

const resultColor = (action) => {
	if (action.includes('error')) {
		return 'error';
	}
	if (action.includes('create')) {
		return 'success';
	}
	return 'default';
};

const ImportResults = ({ result }) => {
	const truncated = result.dryRun && result.foundCount > result.total;

	return (
		<Box sx={{ mt: 2 }}>
			<Alert severity={result.errors ? 'warning' : 'success'} sx={{ mb: 2 }}>
				{result.dryRun ? 'Preview only — no changes were saved. ' : ''}
				{truncated
					? `Previewed the first ${result.total} of ${result.foundCount} rows found. `
					: `Found ${result.foundCount} row(s). `}
				Created: {result.created} &nbsp; Updated: {result.updated} &nbsp; Errors: {result.errors}
				{result.droppedCount ? ` (${result.droppedCount} blank row(s) skipped)` : ''}
			</Alert>
			<TableContainer sx={{ maxHeight: 320 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Email</TableCell>
							<TableCell>Action</TableCell>
							<TableCell>Role</TableCell>
							<TableCell>Groups</TableCell>
							<TableCell>Password</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Error</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{result.results.map((row, i) => (
							<TableRow key={`${row.email}-${i}`}>
								<TableCell>{row.email}</TableCell>
								<TableCell><Chip size="small" color={resultColor(row.action)} label={row.action} /></TableCell>
								<TableCell>{row.role}</TableCell>
								<TableCell>{(row.groups || []).join(', ')}</TableCell>
								<TableCell sx={{ fontFamily: 'monospace' }}>{row.generatedPassword || ''}</TableCell>
								<TableCell>{row.status}</TableCell>
								<TableCell sx={{ color: 'error.main' }}>{row.error}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
};

export const ImportUsersButton = () => {
	const [ open, setOpen ] = useState( false );
	const [ file, setFile ] = useState( null );
	const [ dryRun, setDryRun ] = useState( true );
	const [ loading, setLoading ] = useState( false );
	const [ result, setResult ] = useState( null );
	const fileInputRef = useRef( null );
	const dataProvider = useDataProvider();
	const notify = useNotify();
	const refresh = useRefresh();

	const handleOpen = () => setOpen( true );

	const handleClose = () => {
		setOpen( false );
		setFile( null );
		setResult( null );
		setDryRun( true );
		if ( fileInputRef.current ) {
			fileInputRef.current.value = '';
		}
	};

	const handleFileChange = ( event ) => {
		setFile( event.target.files?.[0] || null );
		setResult( null );
	};

	const handleRun = async () => {
		if ( !file ) {
			return;
		}

		setLoading( true );
		setResult( null );

		try {
			const csv = await file.text();
			const { data } = await dataProvider.importUsers( { csv, dryRun } );
			setResult( { ...data, dryRun } );

			if ( !dryRun ) {
				notify(
					`Import complete: ${data.created} created, ${data.updated} updated, ${data.errors} error(s)`,
					{ type: data.errors ? 'warning' : 'success' }
				);
				refresh();
			}
		} catch ( error ) {
			notify( error.body?.message || error.message || 'Import failed', { type: 'error' } );
		} finally {
			setLoading( false );
		}
	};

	return (
		<>
			<ToolbarButton label="Import" onClick={handleOpen}>
				<UploadFileIcon />
			</ToolbarButton>
			<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
				<DialogTitle>Import Users</DialogTitle>
				<DialogContent>
					<ImportInstructions />
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
						<Button variant="outlined" component="label">
							Choose CSV File
							<input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={handleFileChange} />
						</Button>
						<Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
							{file ? file.name : 'No file selected'}
						</Typography>
						<FormControlLabel
							control={
								<Checkbox
									checked={dryRun}
									onChange={(event) => { setDryRun( event.target.checked ); setResult( null ); }}
								/>
							}
							label="Preview only, no changes saved"
						/>
					</Box>
					{result && <ImportResults result={result} />}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Close</Button>
					<Button
						variant="contained"
						disabled={!file || loading}
						onClick={handleRun}
						startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
					>
						{loading ? 'Running…' : dryRun ? 'Preview' : 'Import'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};